import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Compass, RefreshCw, Save } from "lucide-react";
import type { Agent } from "@/types";
import { SectionCard } from "@/components/SectionCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useDiscoveryProfile } from "@/api/queries/discovery";
import {
  useSetDiscoveryProfile,
  useSetDiscoverySettings,
  useReindexDiscovery,
} from "@/api/mutations/discovery";

// #226: Discovery-Sektion auf der Agent-Detail-Seite. CV-Editor (Markdown) +
// Flags (discoverable/allowRegister/allowConnectionRequest) + Scope + Reindex.
// Die volle CV kommt aus /discovery/profile; die Flags spiegeln AgentResponse.discovery.
export function DiscoverySection({ agent }: { agent: Agent }) {
  const { data: profile } = useDiscoveryProfile(agent.id);
  const setProfile = useSetDiscoveryProfile();
  const setSettings = useSetDiscoverySettings();
  const reindex = useReindexDiscovery();

  // Lokaler CV-Entwurf (dirty-Tracking) — erst aus dem Profil hydrieren.
  const [cv, setCv] = useState("");
  const [cvDirty, setCvDirty] = useState(false);
  useEffect(() => {
    if (profile && !cvDirty) setCv(profile.cv ?? "");
  }, [profile, cvDirty]);

  // Scope-Entwurf (admin-only serverseitig).
  const [scope, setScope] = useState("");
  const [scopeDirty, setScopeDirty] = useState(false);
  useEffect(() => {
    if (profile && !scopeDirty) setScope(profile.scope);
  }, [profile, scopeDirty]);

  // Flags: Profil bevorzugt (frisch), sonst AgentResponse als Sofort-Anzeige.
  // Optional-Chaining + Defaults: robust gegen Version-Skew (älterer Master ohne
  // `agent.discovery` im Response) — sonst würde die Section crashen.
  const flags = {
    discoverable: profile?.discoverable ?? agent.discovery?.discoverable ?? true,
    allowRegister: profile?.allowRegister ?? agent.discovery?.allowRegister ?? true,
    allowConnectionRequest:
      profile?.allowConnectionRequest ?? agent.discovery?.allowConnectionRequest ?? false,
  };

  function toggleFlag(key: keyof typeof flags, value: boolean, label: string) {
    setSettings.mutate(
      { agentId: agent.id, patch: { [key]: value } },
      {
        onSuccess: () => toast.success(`${label}: ${value ? "an" : "aus"}`),
        onError: (err) => toast.error(`${label} fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function saveCv() {
    setProfile.mutate(
      { agentId: agent.id, cvText: cv },
      {
        onSuccess: () => {
          setCvDirty(false);
          toast.success(cv.trim() === "" ? "CV gelöscht" : "CV gespeichert + indiziert");
        },
        onError: (err) => toast.error(`CV speichern fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  function saveScope() {
    setSettings.mutate(
      { agentId: agent.id, patch: { scope: scope.trim() } },
      {
        onSuccess: () => { setScopeDirty(false); toast.success("Scope gespeichert"); },
        onError: (err) => toast.error(`Scope fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  return (
    <SectionCard
      icon={<Compass className="h-[18px] w-[18px]" />}
      title="Discovery"
      description="CV-Profil für die semantische Agent-Suche + Sichtbarkeit"
      action={
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            reindex.mutate(agent.id, {
              onSuccess: () => toast.success("CV neu indiziert"),
              onError: (err) => toast.error(`Reindex fehlgeschlagen: ${(err as Error).message}`),
            })
          }
          disabled={reindex.isPending}
          data-testid="discovery-reindex"
        >
          <RefreshCw className={reindex.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          Neu indizieren
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Flags */}
        <div className="space-y-2.5">
          <FlagRow
            label="Auffindbar"
            hint="Andere Agents können diesen Agenten in der Suche finden."
            checked={flags.discoverable}
            disabled={setSettings.isPending}
            onChange={(v) => toggleFlag("discoverable", v, "Auffindbar")}
            testId="flag-discoverable"
          />
          <FlagRow
            label="Selbst-Registrierung"
            hint="Der Agent darf seine eigene CV per Tool aktualisieren."
            checked={flags.allowRegister}
            disabled={setSettings.isPending}
            onChange={(v) => toggleFlag("allowRegister", v, "Selbst-Registrierung")}
            testId="flag-allowRegister"
          />
          <FlagRow
            label="Autonomes Verbinden"
            hint="Der Agent darf selbst Connection-Requests an andere Agents stellen (Approval bleibt beim Ziel-Owner)."
            checked={flags.allowConnectionRequest}
            disabled={setSettings.isPending}
            onChange={(v) => toggleFlag("allowConnectionRequest", v, "Autonomes Verbinden")}
            testId="flag-allowConnectionRequest"
          />
        </div>

        {/* Scope */}
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="mb-1 block text-[12px] font-semibold text-text2">Scope (Sichtbarkeitsgrenze)</label>
            <Input
              value={scope}
              onChange={(e) => { setScope(e.target.value); setScopeDirty(true); }}
              placeholder="default"
              data-testid="discovery-scope"
            />
          </div>
          <Button variant="outline" size="sm" onClick={saveScope} disabled={!scopeDirty || setSettings.isPending}>
            Scope speichern
          </Button>
        </div>

        {/* CV-Editor */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-[12px] font-semibold text-text2">CV (Markdown)</label>
            {profile?.capabilities && (
              <span className="text-[11px] text-text3">Tagline: {profile.capabilities}</span>
            )}
          </div>
          <Textarea
            value={cv}
            onChange={(e) => { setCv(e.target.value); setCvDirty(true); }}
            rows={10}
            placeholder="# Rolle&#10;&#10;Skills, Wissen, Projekte — woran kann dieser Agent helfen?"
            className="mono text-[12.5px]"
            data-testid="discovery-cv"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-text3">Speichern indiziert die CV neu. Leeres Feld = aus der Suche entfernen.</span>
            <Button size="sm" onClick={saveCv} disabled={!cvDirty || setProfile.isPending} data-testid="discovery-save-cv">
              <Save className="h-4 w-4" />
              CV speichern
            </Button>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

function FlagRow({
  label, hint, checked, disabled, onChange, testId,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 accent-accent"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      <span className="min-w-0">
        <span className="block text-[13px] font-semibold text-text">{label}</span>
        <span className="block text-[11.5px] text-text3">{hint}</span>
      </span>
    </label>
  );
}
